/**
 * PWA install prompt (P1-T18).
 *
 * - Android/Chrome: captures `beforeinstallprompt`, shows a dismissible
 *   "Install Loomia" banner. Dismissal persists in localStorage.
 * - iOS Safari: no beforeinstallprompt — show a one-time "Add to Home
 *   Screen" hint explaining the Share-sheet flow. iOS 26+ may fire
 *   beforeinstallprompt too; the banner path handles it if present.
 * - Never shown when already running as an installed PWA.
 */

const DISMISSED_KEY = 'loomia:pwa-install-dismissed';
const IOS_HINT_KEY = 'loomia:pwa-ios-hint-shown';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandalone(): boolean {
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  // iOS Safari standalone flag.
  if ((window.navigator as { standalone?: boolean }).standalone === true) return true;
  // Launched from an Android home-screen shortcut (TWA/Custom Tab referrer).
  return document.referrer.startsWith('android-app://');
}

function isIOS(): boolean {
  const ua = window.navigator.userAgent;
  const iDevice = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ reports as MacIntel with touch support.
  const iPadOS =
    window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1;
  return iDevice || iPadOS;
}

function el(): HTMLElement {
  let node = document.getElementById('pwa-install');
  if (!node) {
    node = document.createElement('div');
    node.id = 'pwa-install';
    node.className = 'pwa-install';
    node.setAttribute('role', 'dialog');
    node.setAttribute('aria-label', 'Install Loomia');
    document.body.appendChild(node);
  }
  return node;
}

function showBanner(opts: {
  title: string;
  subHtml: string;
  installLabel: string | null;
  onInstall: (() => void) | null;
  dismissKey: string;
}): void {
  const node = el();
  node.innerHTML = `
    <div class="pwa-install-icon"><img src="/icons/icon-192.png" alt="" /></div>
    <div class="pwa-install-copy">
      <p class="pwa-install-title">${opts.title}</p>
      <p class="pwa-install-sub">${opts.subHtml}</p>
    </div>
    <div class="pwa-install-actions">
      ${
        opts.installLabel
          ? `<button class="pwa-install-install" type="button">${opts.installLabel}</button>`
          : ''
      }
      <button class="pwa-install-dismiss" type="button">Not now</button>
    </div>
  `;
  const dismiss = () => {
    try {
      localStorage.setItem(opts.dismissKey, '1');
    } catch {
      /* storage unavailable — banner just reappears next visit */
    }
    node.classList.remove('show');
  };
  node.querySelector('.pwa-install-dismiss')?.addEventListener('click', dismiss);
  const installBtn = node.querySelector('.pwa-install-install');
  if (installBtn && opts.onInstall) {
    installBtn.addEventListener('click', () => {
      opts.onInstall?.();
      dismiss();
    });
  }
  requestAnimationFrame(() => node.classList.add('show'));
}

function dismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

/** Entry point — call once from boot(). */
export function initInstallPrompt(): void {
  if (isStandalone()) return;

  let deferred: BeforeInstallPromptEvent | null = null;

  window.addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    if (dismissed()) return;
    showBanner({
      title: 'Install Loomia',
      subHtml: 'Add it to your home screen for the full-screen studio.',
      installLabel: 'Install',
      onInstall: () => {
        void deferred
          ?.prompt()
          .catch(() => undefined)
          .then(() => deferred?.userChoice)
          .catch(() => undefined);
        deferred = null;
      },
      dismissKey: DISMISSED_KEY,
    });
  });

  // iOS Safari has no beforeinstallprompt: explain the manual flow once.
  if (isIOS() && !dismissed()) {
    let shown = false;
    try {
      shown = localStorage.getItem(IOS_HINT_KEY) === '1';
    } catch {
      /* ignore */
    }
    if (!shown) {
      // Wait a beat so it doesn't fight the first paint.
      window.setTimeout(() => {
        if (isStandalone() || deferred) return;
        showBanner({
          title: 'Install Loomia',
          subHtml:
            'Tap <span class="share-glyph">⤴</span> Share, then “Add to Home Screen”.',
          installLabel: null,
          onInstall: null,
          dismissKey: IOS_HINT_KEY,
        });
      }, 2500);
    }
  }
}
